import type {
  AgentTemplate,
  AgentTemplateAddGrantBody,
  AgentTemplateCreatedGrant,
} from '@reach/shared-types/agent-templates';
import { AGENT_TEMPLATE_VOICE_SYNC_ENQUEUE_FAILED_CODE } from '@reach/shared-types/agent-templates';
import { partnerApi, type ApiError, type ApiResponse, type ToolDefinition } from '../../../lib/api';

export type GrantableTool = Pick<ToolDefinition, 'id' | 'name' | 'display_name' | 'description'>;

const BASE = '/partner/agent-templates';
const TOOL_PAGE_LIMIT = 100;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export async function fetchAgentTemplates(): Promise<AgentTemplate[]> {
  const response = await partnerApi.request<ApiResponse<AgentTemplate[]>>(BASE);
  return response.data;
}

export async function addAgentTemplateGrant(
  templateId: string,
  body: AgentTemplateAddGrantBody,
): Promise<AgentTemplateCreatedGrant> {
  const response = await partnerApi.request<ApiResponse<AgentTemplateCreatedGrant>>(
    `${BASE}/${templateId}/grants`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  return response.data;
}

export async function removeAgentTemplateGrant(templateId: string, grantId: string): Promise<void> {
  await partnerApi.request<void>(`${BASE}/${templateId}/grants/${grantId}`, { method: 'DELETE' });
}

export async function fetchActiveTools(): Promise<GrantableTool[]> {
  const activeTools: GrantableTool[] = [];
  let cursor: string | undefined;
  let morePagesRemain = true;

  while (morePagesRemain) {
    const page = await partnerApi.getTools({ status: 'active', limit: TOOL_PAGE_LIMIT, cursor });
    activeTools.push(...page.tools);
    const nextCursor = page.pagination?.cursor ?? undefined;
    morePagesRemain = page.pagination?.hasNextPage === true && nextCursor !== undefined;
    cursor = nextCursor;
  }

  return activeTools;
}

/** True when the grant was written but its Voice synchronization job could not be queued. */
export function isVoiceSyncEnqueueFailure(error: unknown): boolean {
  const envelope = (error as ApiError | undefined)?.response?.data?.data;
  return isObject(envelope) && envelope.code === AGENT_TEMPLATE_VOICE_SYNC_ENQUEUE_FAILED_CODE;
}
